/**
 * Validates if a date string is a valid and reasonable date
 * Checks for:
 * 1. Valid ISO format
 * 2. Dates that are suspiciously old (like year 0001)
 * 3. Proper timezone offset format
 *
 * @param dateString The date string to validate
 * @returns An object with isValid flag and optional error message
 */
export const validateTimeFormat = (
  dateString: string
): { isValid: boolean, error?: string } => {
  // Check if the string is empty or null
  if (!dateString) {
    return { isValid: false, error: 'Date string is empty' };
  }

  // Try to parse the date
  const date = new Date(dateString);

  // Check if date is valid
  if (isNaN(date.getTime())) {
    return { isValid: false, error: 'Invalid date format' };
  }

  // Check for suspiciously old dates (before 1900)
  if (date.getFullYear() < 1900) {
    return { isValid: false, error: 'Date is unreasonably old (before 1900)' };
  }

  // Check timezone format if present
  // Only check timezone if the string contains T (indicating time portion) and then + or -
  if (
    dateString.includes('T') &&
    (dateString.indexOf('+', dateString.indexOf('T')) !== -1 ||
      dateString.indexOf('-', dateString.indexOf('T')) !== -1)
  ) {
    const timezoneRegex = /([+-])(\d{2}):?(\d{2})$/;
    const match = dateString.match(timezoneRegex);

    if (!match) {
      return { isValid: false, error: 'Invalid timezone format' };
    }

    const hours = parseInt(match[2], 10);
    const minutes = parseInt(match[3], 10);

    // Check that timezone offset is within valid range
    if (hours > 23 || minutes > 59) {
      return { isValid: false, error: 'Invalid timezone offset' };
    }
  }

  return { isValid: true };
};
/**
 * Helper function to format a date or return fallback if invalid
 *
 * @param dateString The date string to format
 * @param formatter Function to format valid dates
 * @param fallback Value to return for invalid dates
 * @returns Formatted date or fallback value
 */
export const safeFormatDate = (
  dateString: string | undefined,
  formatter: (date: Date) => string,
  fallback: string = '-'
): string => {
  if (!dateString) return fallback;

  const validation = validateTimeFormat(dateString);
  if (!validation.isValid) return fallback;

  return formatter(new Date(dateString));
};
